/**
 * @fileoverview Blurb generation schemas for API validation
 */

import { z } from '@hono/zod-openapi';

/**
 * Valid tone values for blurb generation
 */
export const BlurbToneSchema = z
  .enum(['agree', 'disagree', 'skeptical', 'excited', 'neutral'])
  .openapi({
    description: 'Desired tone for the generated blurb',
    example: 'agree',
  });

/**
 * Valid tone values as an array for documentation
 */
export const BLURB_TONES = ['agree', 'disagree', 'skeptical', 'excited', 'neutral'];

/**
 * Single blurb alternative with confidence and metadata
 */
export const BlurbAlternativeSchema = z
  .object({
    blurb: z.string().openapi({
      description: 'Generated blurb text',
      example:
        'Yan Cui does a deep dive into Lambda cold starts. I totally agree with his take on memory optimization...',
    }),
    confidence: z.number().min(0).max(1).openapi({
      description: 'AI confidence in this alternative (0-1)',
      example: 0.85,
    }),
    keyPointsUsed: z
      .array(z.string())
      .optional()
      .openapi({
        description: 'Which key points were incorporated',
      }),
    toneMatch: z.enum(['strong', 'moderate', 'weak']).openapi({
      description: 'How well the blurb matches the requested tone',
      example: 'strong',
    }),
    suggestion: z.string().optional().openapi({
      description: 'Suggestion for potential improvement',
      example: 'Consider adding a personal anecdote about cold starts',
    }),
  })
  .openapi('BlurbAlternative');

/**
 * Request schema for single blurb generation
 */
export const GenerateSingleBlurbRequestSchema = z
  .object({
    itemHash: z
      .string()
      .regex(/^[0-9a-z]{12}$/)
      .openapi({
        description: 'Hash of the canonical item',
        example: 'abc123def456',
      }),
    selectedKeyPoints: z
      .array(z.string())
      .optional()
      .openapi({
        description: 'Selected AI extracts to incorporate',
        example: ['Cold starts are overblown', 'Memory optimization matters'],
      }),
    tone: BlurbToneSchema.default('neutral'),
    category: z.string().openapi({
      description: 'Newsletter category',
      example: 'reads',
    }),
    feedback: z.string().optional().openapi({
      description: 'User feedback for regeneration',
    }),
  })
  .openapi('GenerateSingleBlurbRequest');

/**
 * Response schema for single blurb generation
 */
export const BlurbGenerationResponseSchema = z
  .object({
    itemHash: z.string(),
    category: z.string(),
    tone: BlurbToneSchema,
    alternatives: z.array(BlurbAlternativeSchema).min(2).max(3),
    reasoning: z.string().openapi({
      description: 'AI reasoning for the approach taken',
    }),
    usage: z.object({
      costUsd: z.number().optional(),
      tokensUsed: z.number(),
    }),
    sessionId: z.string().optional().openapi({
      description: 'Session ID for regeneration',
    }),
  })
  .openapi('BlurbGenerationResponse');

/**
 * News section item input
 */
export const NewsSectionItemSchema = z
  .object({
    itemHash: z.string().regex(/^[0-9a-z]{12}$/),
    selectedKeyPoints: z.array(z.string()).optional(),
    tone: BlurbToneSchema.optional().default('neutral'),
  })
  .openapi('NewsSectionItem');

/**
 * Request schema for news section generation
 */
export const GenerateNewsSectionRequestSchema = z
  .object({
    items: z
      .array(NewsSectionItemSchema)
      .min(1)
      .max(20)
      .openapi({
        description: 'Ordered list of items for the news section',
      }),
  })
  .openapi('GenerateNewsSectionRequest');

/**
 * Single blurb in news section response
 */
export const NewsSectionBlurbSchema = z
  .object({
    itemHash: z.string(),
    blurb: z.string(),
    transition: z.string().optional().openapi({
      description: 'Transition phrase to next item',
    }),
  })
  .openapi('NewsSectionBlurb');

/**
 * Response schema for news section generation
 */
export const NewsSectionResponseSchema = z
  .object({
    sectionHeader: z.string().openapi({
      description: 'Section header with emoji',
      example: '### Serverless News & Announcements 📣',
    }),
    blurbs: z.array(NewsSectionBlurbSchema),
    sectionNotes: z.string().optional().openapi({
      description: 'Notes about section flow and voice consistency',
    }),
    usage: z.object({
      costUsd: z.number().optional(),
      tokensUsed: z.number(),
    }),
    sessionId: z.string().optional(),
  })
  .openapi('NewsSectionResponse');

/**
 * Request schema for issue intro generation
 */
export const GenerateIssueIntroRequestSchema = z
  .object({
    newsSection: z.string().min(100).openapi({
      description: 'Completed news section text',
    }),
    highlightItemHashes: z
      .array(z.string().regex(/^[0-9a-z]{12}$/))
      .length(3)
      .openapi({
        description: 'Exactly 3 item hashes to highlight',
      }),
    issueNumber: z.number().int().positive().openapi({
      description: 'Issue number',
      example: 352,
    }),
  })
  .openapi('GenerateIssueIntroRequest');

/**
 * Title option in issue intro response
 */
export const TitleOptionSchema = z
  .object({
    title: z.string(),
    rationale: z.string().optional().openapi({
      description: 'Why this title works',
    }),
  })
  .openapi('TitleOption');

/**
 * Response schema for issue intro generation
 */
export const IssueIntroResponseSchema = z
  .object({
    introLine: z.string().openapi({
      description: 'The "In this issue..." intro paragraph',
      example:
        "In this issue, we've got Lambda updates, DynamoDB patterns, and why cold starts might not be as bad as you think.",
    }),
    titleOptions: z.array(TitleOptionSchema).min(3).max(3),
    issueNumber: z.number(),
    usage: z.object({
      costUsd: z.number().optional(),
      tokensUsed: z.number(),
    }),
    sessionId: z.string().optional(),
  })
  .openapi('IssueIntroResponse');

/**
 * Request schema for deep dive generation
 */
export const GenerateDeepDiveRequestSchema = z
  .object({
    itemHashes: z
      .array(z.string().regex(/^[0-9a-z]{12}$/))
      .min(1)
      .max(5)
      .openapi({
        description: 'Item hashes to reference as evidence',
      }),
    userCommentary: z.string().min(10).openapi({
      description: 'User position/commentary to expand',
      example: 'I think the serverless vs containers debate misses the point...',
    }),
    evidenceGuidance: z.string().optional().openapi({
      description: 'How to use evidence from items',
      example: 'Use first article to support, second to contrast',
    }),
    section: z.enum(['deep-dive', 'opinion']).default('opinion'),
  })
  .openapi('GenerateDeepDiveRequest');

/**
 * Single paragraph in deep dive response
 */
export const DeepDiveParagraphSchema = z
  .object({
    content: z.string(),
    sourcesReferenced: z
      .array(z.number().int().positive())
      .optional()
      .openapi({
        description: 'Indices of sources referenced (1-based)',
      }),
  })
  .openapi('DeepDiveParagraph');

/**
 * Response schema for deep dive generation
 */
export const DeepDiveResponseSchema = z
  .object({
    title: z.string().openapi({
      description: 'Section title/header',
    }),
    paragraphs: z.array(DeepDiveParagraphSchema).min(3).max(5),
    suggestedEdits: z
      .array(z.string())
      .optional()
      .openapi({
        description: 'Suggestions for strengthening the piece',
      }),
    usage: z.object({
      costUsd: z.number().optional(),
      tokensUsed: z.number(),
    }),
    sessionId: z.string().optional(),
  })
  .openapi('DeepDiveResponse');

/**
 * Request schema for regeneration
 */
export const RegenerateRequestSchema = z
  .object({
    sessionId: z.string().openapi({
      description: 'Session ID from previous generation',
    }),
    feedback: z.string().min(5).openapi({
      description: 'User feedback for regeneration',
      example: 'Make it shorter and more skeptical',
    }),
    originalType: z.enum(['single-blurb', 'news-section', 'issue-intro', 'deep-dive']).openapi({
      description: 'Type of content being regenerated',
    }),
  })
  .openapi('RegenerateRequest');

/**
 * Response schema for regeneration
 */
export const RegenerateResponseSchema = z
  .object({
    regenerated: z.any().openapi({
      description: 'Regenerated content matching original type structure',
    }),
    originalType: z.string(),
    usage: z.object({
      costUsd: z.number().optional(),
      tokensUsed: z.number(),
    }),
    sessionId: z.string().optional(),
  })
  .openapi('RegenerateResponse');
